import { animate, inView } from 'motion';
import { prefersReducedMotion } from './index';

interface FadeInUpOptions {
  stagger?: number;
  baseDelay?: number;
  duration?: number;
  margin?: string;
  cardsAnimateWithParent?: boolean;
}

// Track which elements have been initialized to prevent duplicate setup
const initializedElements = new WeakSet();

// Track which elements are currently being animated to prevent multiple animations
const animatingElements = new WeakSet();

export const initFadeInUpAnimation = (selector = '.fade-in-up', options: FadeInUpOptions = {}) => {
  const {
    stagger = 0.05,
    baseDelay = 0.1,
    duration = 0.7,
    margin = '0px 0px -20% 0px',
    cardsAnimateWithParent = true
  } = options;

  const elements = document.querySelectorAll(selector);

  if (prefersReducedMotion()) {
    return;
  }

  // First pass: identify containers and their cards
  const containerElements: HTMLElement[] = [];
  const cardElements = new Set<HTMLElement>();

  elements.forEach(element => {
    const elementEl = element as HTMLElement;
    const cards = findCardsInElement(elementEl);

    if (cards.length > 0) {
      // This is a container
      containerElements.push(elementEl);
      // Mark all cards as belonging to a container
      cards.forEach(card => cardElements.add(card as HTMLElement));
    }
  });

  // Second pass: process elements, skipping cards that belong to containers
  elements.forEach(element => {
    const elementEl = element as HTMLElement;

    // Skip if this element has already been initialized
    if (initializedElements.has(elementEl)) {
      return;
    }

    // Skip if this is a card that belongs to a container
    if (cardElements.has(elementEl)) {
      return;
    }

    // Mark as initialized
    initializedElements.add(elementEl);

    // Skip if already animated and visible
    if (elementEl.dataset.animated === 'true') {
      const currentOpacity = parseFloat(getComputedStyle(elementEl).opacity);
      if (currentOpacity > 0.1) {
        return;
      }
    }

    // Check if this element contains cards that need staggered animation
    const cards = findCardsInElement(elementEl);

    if (cards.length > 0) {
      // This element contains cards
      if (cardsAnimateWithParent) {
        // Container and cards animate together
        setupContainerWithCardsAnimation(element, elementEl, cards, {
          duration,
          stagger,
          margin
        });
      } else {
        // Container animates separately from cards
        setupContainerOnlyAnimation(element, elementEl, {
          duration,
          margin
        });

        // Set up individual card animations
        setupIndividualCardAnimations(cards, {
          duration,
          baseDelay,
          margin
        });
      }
    } else {
      // This element has no cards - simple single element animation
      setupSingleElementAnimation(element, elementEl, {
        duration,
        baseDelay,
        margin
      });
    }
  });
};

function setupContainerWithCardsAnimation(
  element: Element,
  elementEl: HTMLElement,
  cards: Element[],
  options: { duration: number; stagger: number; margin: string }
) {
  const { duration, stagger, margin } = options;

  inView(
    element,
    () => {
      // Skip if element is already being animated
      if (animatingElements.has(elementEl)) {
        return;
      }

      // Mark as being animated
      animatingElements.add(elementEl);

      requestAnimationFrame(() => {
        // Skip if container is already animated and visible
        if (elementEl.dataset.animated === 'true') {
          const containerOpacity = parseFloat(getComputedStyle(elementEl).opacity);
          if (containerOpacity > 0.1) {
            animatingElements.delete(elementEl);
            return;
          }
        }

        // CSS already sets initial state via .fade-in-up class
        // Only animate from current computed values to final state
        const computedStyle = getComputedStyle(elementEl);
        const currentOpacity = parseFloat(computedStyle.opacity) || 0;
        const currentTransform = computedStyle.transform;

        // Animate the container first
        animate(
          element,
          {
            opacity: [currentOpacity, 1],
            transform: [currentTransform === 'none' ? 'translateY(20px)' : currentTransform, 'translateY(0px)']
          },
          {
            duration: duration * 0.8,
            ease: 'easeInOut',
            onComplete: () => {
              elementEl.dataset.animated = 'true';
              animatingElements.delete(elementEl);
            }
          }
        );
      });
    },
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    { margin: margin as any }
  );

  // Set up individual inView for each card
  cards.forEach((card, cardIndex) => {
    const cardEl = card as HTMLElement;

    // Skip if this card has already been initialized
    if (initializedElements.has(cardEl)) {
      return;
    }

    // Mark card as initialized
    initializedElements.add(cardEl);

    inView(
      card,
      () => {
        // Skip if card is already being animated
        if (animatingElements.has(cardEl)) {
          return;
        }

        // Mark as being animated
        animatingElements.add(cardEl);

        requestAnimationFrame(() => {
          // Skip if card is already animated and visible
          if (cardEl.dataset.animated === 'true') {
            const cardOpacity = parseFloat(getComputedStyle(cardEl).opacity);
            if (cardOpacity > 0.1) {
              animatingElements.delete(cardEl);
              return;
            }
          }

          // CSS already sets initial state via .fade-in-up class
          // Only animate from current computed values to final state
          const computedStyle = getComputedStyle(cardEl);
          const currentOpacity = parseFloat(computedStyle.opacity) || 0;
          const currentTransform = computedStyle.transform;

          // Add stagger delay based on card index
          const cardDelay = cardIndex * stagger;

          animate(
            card,
            {
              opacity: [currentOpacity, 1],
              transform: [currentTransform === 'none' ? 'translateY(20px)' : currentTransform, 'translateY(0px)']
            },
            {
              duration,
              delay: cardDelay,
              ease: 'easeOut',
              onComplete: () => {
                cardEl.dataset.animated = 'true';
                animatingElements.delete(cardEl);
              }
            }
          );
        });
      },
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      { margin: margin as any }
    );
  });
}

function setupContainerOnlyAnimation(
  element: Element,
  elementEl: HTMLElement,
  options: { duration: number; margin: string }
) {
  const { duration, margin } = options;

  inView(
    element,
    () => {
      // Skip if element is already being animated
      if (animatingElements.has(elementEl)) {
        return;
      }

      // Mark as being animated
      animatingElements.add(elementEl);

      requestAnimationFrame(() => {
        // Skip if container is already animated and visible
        if (elementEl.dataset.animated === 'true') {
          const containerOpacity = parseFloat(getComputedStyle(elementEl).opacity);
          if (containerOpacity > 0.1) {
            animatingElements.delete(elementEl);
            return;
          }
        }

        // CSS already sets initial state via .fade-in-up class
        // Only animate from current computed values to final state
        const computedStyle = getComputedStyle(elementEl);
        const currentOpacity = parseFloat(computedStyle.opacity) || 0;
        const currentTransform = computedStyle.transform;

        // Animate only the container
        animate(
          element,
          {
            opacity: [currentOpacity, 1],
            transform: [currentTransform === 'none' ? 'translateY(20px)' : currentTransform, 'translateY(0px)']
          },
          {
            duration: duration * 0.8,
            ease: 'easeOut',
            onComplete: () => {
              elementEl.dataset.animated = 'true';
              animatingElements.delete(elementEl);
            }
          }
        );
      });
    },
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    { margin: margin as any }
  );
}

function setupIndividualCardAnimations(
  cards: Element[],
  options: { duration: number; baseDelay: number; margin: string }
) {
  const { duration, baseDelay, margin } = options;

  cards.forEach(card => {
    const cardEl = card as HTMLElement;

    // Skip if this card has already been initialized
    if (initializedElements.has(cardEl)) {
      return;
    }

    // Mark card as initialized
    initializedElements.add(cardEl);

    inView(
      card,
      () => {
        // Skip if card is already being animated
        if (animatingElements.has(cardEl)) {
          return;
        }

        // Mark as being animated
        animatingElements.add(cardEl);

        requestAnimationFrame(() => {
          // Skip if card is already animated and visible
          if (cardEl.dataset.animated === 'true') {
            const cardOpacity = parseFloat(getComputedStyle(cardEl).opacity);
            if (cardOpacity > 0.1) {
              animatingElements.delete(cardEl);
              return;
            }
          }

          // CSS already sets initial state via .fade-in-up class
          // Only animate from current computed values to final state
          const computedStyle = getComputedStyle(cardEl);
          const currentOpacity = parseFloat(computedStyle.opacity) || 0;
          const currentTransform = computedStyle.transform;

          animate(
            card,
            {
              opacity: [currentOpacity, 1],
              transform: [currentTransform === 'none' ? 'translateY(20px)' : currentTransform, 'translateY(0px)']
            },
            {
              duration,
              delay: baseDelay,
              ease: 'easeOut',
              onComplete: () => {
                cardEl.dataset.animated = 'true';
                animatingElements.delete(cardEl);
              }
            }
          );
        });
      },
      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      { margin: margin as any }
    );
  });
}

function setupSingleElementAnimation(
  element: Element,
  elementEl: HTMLElement,
  options: { duration: number; baseDelay: number; margin: string }
) {
  const { duration, baseDelay, margin } = options;

  inView(
    element,
    () => {
      // Skip if element is already being animated
      if (animatingElements.has(elementEl)) {
        return;
      }

      // Mark as being animated
      animatingElements.add(elementEl);

      requestAnimationFrame(() => {
        // Skip if container is already animated and visible
        if (elementEl.dataset.animated === 'true') {
          const containerOpacity = parseFloat(getComputedStyle(elementEl).opacity);
          if (containerOpacity > 0.1) {
            animatingElements.delete(elementEl);
            return;
          }
        }

        // CSS already sets initial state via .fade-in-up class
        // Only animate from current computed values to final state
        const computedStyle = getComputedStyle(elementEl);
        const currentOpacity = parseFloat(computedStyle.opacity) || 0;
        const currentTransform = computedStyle.transform;

        animate(
          element,
          {
            opacity: [currentOpacity, 1],
            transform: [currentTransform === 'none' ? 'translateY(20px)' : currentTransform, 'translateY(0px)']
          },
          {
            duration,
            delay: baseDelay,
            ease: 'easeOut',
            onComplete: () => {
              elementEl.dataset.animated = 'true';
              animatingElements.delete(elementEl);
            }
          }
        );
      });
    },
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    { margin: margin as any }
  );
}

/**
 * Finds cards within an element that should be animated with stagger
 */
function findCardsInElement(element: HTMLElement): Element[] {
  const elementClass = element.className;

  // Check if this is a related-row section (which contains cards)
  if (elementClass.includes('related-row')) {
    // Find cards within this related-row section
    const cardElements = element.querySelectorAll(
      '.related-card.fade-in-up, .small-content-card.fade-in-up, .logo-card.fade-in-up, .product-card.fade-in-up'
    );
    return Array.from(cardElements);
  }

  // Check if this is a listing-grid (which contains cards)
  if (elementClass.includes('listing-grid')) {
    const cardElements = element.querySelectorAll(
      '.related-card.fade-in-up, .small-content-card.fade-in-up, .logo-card.fade-in-up, .product-card.fade-in-up'
    );
    return Array.from(cardElements);
  }

  // For other containers, look for nested fade-in-up elements that are cards
  const nestedElements = element.querySelectorAll('.fade-in-up');
  return Array.from(nestedElements).filter(el => {
    const elClass = (el as HTMLElement).className;
    // Only include elements that are actually cards, not containers
    return (
      elClass.includes('related-card') ||
      elClass.includes('small-content-card') ||
      elClass.includes('logo-card') ||
      elClass.includes('product-card')
    );
  });
}
